<template>
  <DatePicker
    v-bind.prop="props"
    v-model="modelValue"
    :pt="passThrough"
    :unstyled="false"
    v-on="listeners"
    @value-change="($event) => $emit('value-change', $event)"
    @update:modelValue="($event) => $emit('update:modelValue', $event)"
    class="meridia-date-picker"
  >
    <template
      v-for="name in Object.keys(slots).filter(
        (key) => !['default', 'header'].includes(key)
      )"
      :key="name"
      v-slot:[name]="slotData"
    >
      <slot :name="name" v-bind="slotData || {}"></slot>
    </template>
  </DatePicker>
</template>

<script setup lang="ts">
import {
  DatePickerBlurEvent,
  DatePickerEmitsOptions,
  DatePickerMonthChangeEvent,
  DatePickerPassThroughOptions,
  DatePickerProps,
  DatePickerSlots,
  DatePickerYearChangeEvent,
} from 'primevue';
import { PassThrough } from '@primevue/core';
import { usePassThrough } from 'primevue/passthrough';
import DatePicker from 'primevue/datepicker';
import { useListeners } from '../utils';

const modelValue = defineModel<Date | Date[]>();
const props = withDefaults(defineProps<DatePickerProps>(), {
  modelValue: null,
  selectionMode: 'single',
  inline: false,
  showOtherMonths: true,
  selectOtherMonths: false,
  showIcon: false,
  iconDisplay: 'button',
  numberOfMonths: 1,
  breakpoint: '769px',
  view: 'date',
  showOnFocus: true,
  autoZIndex: true,
  baseZIndex: 0,
  showButtonBar: false,
  shortYearCutoff: '+10',
  showTime: false,
  timeOnly: false,
  hourFormat: '24',
  stepHour: 1,
  stepMinute: 1,
  stepSecond: 1,
  showSeconds: false,
  hideOnDateTimeSelect: false,
  hideOnRangeSelection: false,
  timeSeparator: ':',
  showWeek: false,
  manualInput: true,
  invalid: false,
  disabled: false,
  variant: null,
  readonly: false,
  appendTo: 'body',
  fluid: null,
  unstyled: false,
  size: 'small',
  placeholder: 'Select',
});

const slots = defineSlots<DatePickerSlots>();

const emit = defineEmits({
  'update:modelValue': (
    value: Date | Array<Date> | Array<Date | null> | undefined | null
  ) => true,
  'value-change': (
    value: Date | Array<Date> | Array<Date | null> | undefined | null
  ) => true,
  input: (event: Event) => event instanceof Event,
  'date-select': (value: Date) => value instanceof Date,
  show: () => true,
  hide: () => true,
  'today-click': (date: Date) => date instanceof Date,
  'clear-click': (event: Event) => event instanceof Event,
  'month-change': (event: DatePickerMonthChangeEvent) => true,
  'year-change': (event: DatePickerYearChangeEvent) => true,
  focus: (event: Event) => event instanceof Event,
  blur: (event: DatePickerBlurEvent) => true,
  keydown: (event: Event) => event instanceof Event,
});
const { listeners } = useListeners<DatePickerEmitsOptions>(emit, DatePicker);

const passThrough = usePassThrough(
  props.pt,
  {
    pcInputText: {
      root: {
        class: {
          'meridia-input-wrapper meridia-input': true,
          'size-small': props.size === 'small',
          'size-large': props.size === 'large',
        },
      },
    },
    weekday: {
      class: {
        'text-title-small': true,
      },
    },
    day: () => {
      return {
        class: {
          'text-body-base': true,
        },
      };
    },
  } as DatePickerPassThroughOptions,
  { mergeSections: false, mergeProps: true }
) as PassThrough<DatePickerPassThroughOptions>;
</script>

<style src="../input/style.scss" lang="scss"></style>
